const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libDashboardAccess = require('headlight-dashboard-access');
const libHosts = require('headlight-qrcode/source/Headlight-QRCode-Hosts.js');

const libActions = require('./Headlight-Dashboard-Actions.js');

const CACHE_MILLISECONDS = 60 * 1000;
const BODY_ID = 'HeadlightDashboard-Body';
const PROJECT_PICKER_ID = 'HeadlightDashboard-ProjectPicker';
const PROJECT_PAGE_SIZE = 25;
const RECENT_PROJECTS_LIMIT = 5;
const DEFAULT_LIST_PAGE_SIZE = 100;
const MAX_SWITCHER_LINKS = 4;

/**
 * @param {any} pValue
 * @return {string}
 */
function escapeHTML(pValue)
{
	return String(pValue === undefined || pValue === null ? '' : pValue)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * The hash that means "the list of dashboards" rather than one dashboard. Registry hashes are lowercase, so it can't
 * collide with a real one.
 */
const CHOOSER_HASH = 'All';

/**
 * Route spellings a host registers (and a micro-app allows) for embedded dashboards under `pRouteBase`. Bare
 * `pRouteBase` opens the person's landing dashboard; `<base>/All[/Project/:IDProject]` is the list. `FilteredTo` and
 * `Page` keep a list dashboard's search, sort and paging inside the host, where its role filter, notices and dashboard
 * links stay.
 *
 * @param {string} [pRouteBase]
 * @return {Array<string>}
 */
function routePatterns(pRouteBase)
{
	const tmpBase = pRouteBase || '/Dashboards';
	const tmpPatterns = [ tmpBase ];
	for (const tmpProject of [ '', '/Project/:IDProject' ])
	{
		for (const tmpFilter of [ '', '/FilteredTo/:Filter' ])
		{
			for (const tmpPage of [ '', '/Page/:Offset/:PageSize' ])
			{
				const tmpRoute = `${tmpBase}/:Hash${tmpProject}${tmpFilter}${tmpPage}`;
				tmpPatterns.push(tmpRoute, `${tmpRoute}/FilterExperience/:FilterExperience`);
			}
		}
	}
	return tmpPatterns;
}

/**
 * Read a route or link to a list's own `/PSRS/<RecordSet>/List` view, as its search, sort and page links write it:
 * `[#]/PSRS/<RecordSet>/List[/FilteredTo/<filter>][/<offset>/<size>][/FilterExperience/<x>]`. Parts come back as they
 * appear in the URL (still encoded).
 *
 * @param {string} pRoute
 * @param {string} pRecordSet
 * @return {{ Filter: string, Offset: number, PageSize: number, FilterExperience: string }|null} null when it isn't that list
 */
function parseListLink(pRoute, pRecordSet)
{
	if (typeof pRoute !== 'string')
	{
		return null;
	}
	const tmpPrefix = `/PSRS/${pRecordSet}/List`;
	const tmpRoute = pRoute.replace(/^#/, '');
	if (tmpRoute.indexOf(tmpPrefix) !== 0)
	{
		return null;
	}
	const tmpMatch = /^(?:\/FilteredTo\/([^/]+))?(?:\/(\d+)\/(\d+))?(?:\/FilterExperience\/(.+))?\/?$/.exec(tmpRoute.slice(tmpPrefix.length));
	if (!tmpMatch)
	{
		return null;
	}
	return { Filter: tmpMatch[1] || '', Offset: tmpMatch[2] ? parseInt(tmpMatch[2], 10) : 0, PageSize: tmpMatch[3] ? parseInt(tmpMatch[3], 10) : 0, FilterExperience: tmpMatch[4] || '' };
}

/**
 * @param {string} pFilter - A meadow filter string.
 * @return {Array<string>} its four-part clauses (`FBV~Status~NE~VOID`)
 */
function filterClauses(pFilter)
{
	const tmpTrimmed = String(pFilter || '').replace(/^~+|~+$/g, '');
	const tmpParts = tmpTrimmed ? tmpTrimmed.split('~') : [];
	const tmpClauses = [];
	for (let i = 0; i + 3 < tmpParts.length; i += 4)
	{
		tmpClauses.push(tmpParts.slice(i, i + 4).join('~'));
	}
	return tmpClauses;
}

/**
 * The filter a list dashboard renders with: its configured scope, then what the person searched or sorted by. A sort
 * the person chose replaces the scope's default sort. The list hands back scope + person on its next search or sort, so
 * a leading copy of the scope is dropped rather than repeated.
 *
 * A header click cycles ascending, descending, then no sort; that last step arrives as a page with no sort in it. A
 * dashboard with a default sort never shows an unsorted list: clearing the default column sorts it the other way from
 * how it was last shown, and clearing any other column brings the default sort back.
 *
 * @param {string} pScopeFilter - The dashboard's ScopeFilter, e.g. `FBV~Status~NE~VOID~FSF~DaysSinceSampled~ASC~0`.
 * @param {string} [pFilter] - The route's FilteredTo part.
 * @param {boolean} [pIsPage] - The route names a page (a sort or page link), not only a search or filters.
 * @param {string} [pPreviousFilter] - The filter this list last rendered with.
 * @return {string}
 */
function composeListFilter(pScopeFilter, pFilter, pIsPage, pPreviousFilter)
{
	const fIsSort = (pClause) => pClause.indexOf('FSF~') === 0;
	const tmpScope = filterClauses(pScopeFilter);
	const tmpScopeTests = tmpScope.filter((pClause) => !fIsSort(pClause));
	const tmpPerson = filterClauses(pFilter);
	let tmpSkip = 0;
	while ((tmpSkip < tmpScopeTests.length) && (tmpPerson[tmpSkip] === tmpScopeTests[tmpSkip]))
	{
		tmpSkip++;
	}
	if (tmpSkip < tmpScopeTests.length)
	{
		tmpSkip = 0;
	}
	const tmpPersonTests = tmpPerson.slice(tmpSkip).filter((pClause) => !fIsSort(pClause));
	const tmpPersonSorts = tmpPerson.filter(fIsSort);
	const tmpScopeSorts = tmpScope.filter(fIsSort);
	let tmpSorts = (tmpPersonSorts.length > 0) ? tmpPersonSorts : tmpScopeSorts;
	if ((tmpPersonSorts.length < 1) && pIsPage && (tmpScopeSorts.length > 0))
	{
		const [ , tmpDefaultField ] = tmpScopeSorts[0].split('~');
		const tmpPreviousDefault = filterClauses(pPreviousFilter).filter(fIsSort).find((pClause) => pClause.split('~')[1] === tmpDefaultField);
		if (tmpPreviousDefault)
		{
			const tmpParts = tmpPreviousDefault.split('~');
			const tmpFlipped = `FSF~${tmpDefaultField}~${String(tmpParts[2]).toUpperCase() === 'DESC' ? 'ASC' : 'DESC'}~0`;
			tmpSorts = [ tmpFlipped ].concat(tmpScopeSorts.slice(1));
		}
	}
	return tmpScopeTests.concat(tmpPersonTests, tmpSorts).join('~');
}

/**
 * Hosts dashboards inside any pict app: asks the platform which dashboards this person may open, shows a chooser, and
 * opens one into a container the host owns — project and role filters applied, actions routed to the host.
 *
 * The host owns the route (`routePatterns()`); filter changes navigate back to it, so a micro-app allows one route
 * family rather than every dashboard's recordset.
 */
class HeadlightDashboardHost extends libFableServiceProviderBase
{
	/**
	 * @param {any} pFable
	 * @param {Record<string, any>} [pOptions]
	 * @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({ RouteBase: '/Dashboards', LegacyDashboardsBaseURL: null }, pOptions), pServiceHash);
		/** @type {any} */
		this.pict = this.fable;
		/** @type {any} */
		this.log;
		/** @type {Record<string, { At: number, Value: any }>} */
		this._cache = {};
		/** @type {Record<string, Array<any>>} */
		this._pendingDrillFilters = {};
		this._legacyListenerInstalled = false;
		/** @type {Record<string, { Hash: string, IDProject: number }>} list dashboards by recordset, for `_redirectListRoute` */
		this._listDashboards = {};
		/** @type {Record<string, string>} the filter each list dashboard last rendered with, by recordset */
		this._lastListFilters = {};
		this._listRouteRedirectInstalled = false;
	}

	/**
	 * Whether an open dashboard shows its Description under its title: the host app's `getShowDescriptions` option.
	 *
	 * @return {boolean}
	 */
	_showDescriptions()
	{
		return (typeof this.options.getShowDescriptions === 'function') && (this.options.getShowDescriptions() === true);
	}

	/** @return {any} */
	get actions()
	{
		return this.pict.providers.HeadlightDashboardActions;
	}

	/** @return {any} */
	get loader()
	{
		return this.pict.providers.DynamicDashboardLoader;
	}

	/**
	 * @return {number}
	 */
	_customerID()
	{
		if (typeof this.options.getCustomerID === 'function')
		{
			return parseInt(this.options.getCustomerID(), 10) || 0;
		}
		const tmpAppData = this.pict.AppData || {};
		const tmpSession = tmpAppData.CurrentUser || tmpAppData.Session || (this.pict.providers.SessionProvider && this.pict.providers.SessionProvider.session) || {};
		return parseInt(tmpSession.CustomerID, 10) || 0;
	}

	/**
	 * @param {string} [pHash]
	 * @param {number} [pIDProject]
	 * @param {{ Filter?: string, Offset?: number, PageSize?: number, FilterExperience?: string }} [pPage] - A list's
	 *   search or sort and page; Filter and FilterExperience as they appear in a URL (already encoded).
	 * @return {string}
	 */
	routeFor(pHash, pIDProject, pPage)
	{
		let tmpRoute = this.options.RouteBase;
		if (pHash)
		{
			tmpRoute += `/${encodeURIComponent(pHash)}`;
		}
		if (pIDProject)
		{
			tmpRoute += `/Project/${pIDProject}`;
		}
		if (pPage)
		{
			if (pPage.Filter)
			{
				tmpRoute += `/FilteredTo/${pPage.Filter}`;
			}
			if (pPage.PageSize)
			{
				tmpRoute += `/Page/${pPage.Offset || 0}/${pPage.PageSize}`;
			}
			if (pPage.FilterExperience)
			{
				tmpRoute += `/FilterExperience/${pPage.FilterExperience}`;
			}
		}
		return tmpRoute;
	}

	/**
	 * Navigate to a dashboard (the default Dashboard.Open handling). `pFilters` narrows the next open of that dashboard
	 * — a drill-down from a statewide view into one district — and is not kept in the URL.
	 *
	 * @param {string} pHash
	 * @param {number} [pIDProject]
	 * @param {Array<any>} [pFilters] - Filter clauses, as in DashboardAccess rules.
	 */
	navigateTo(pHash, pIDProject, pFilters)
	{
		if (Array.isArray(pFilters) && pFilters.length > 0)
		{
			this._pendingDrillFilters[pHash] = pFilters;
		}
		const tmpRouter = this.pict.providers.PictRouter;
		if (tmpRouter && typeof tmpRouter.navigate === 'function')
		{
			tmpRouter.navigate(this.routeFor(pHash, pIDProject));
		}
	}

	/**
	 * Which dashboards this person may open (cached for a minute per project).
	 *
	 * @param {number} [pIDProject]
	 * @param {boolean} [pForce]
	 * @return {Promise<any>} the PlatformDashboard/Available response
	 */
	async loadAvailable(pIDProject, pForce)
	{
		const tmpKey = pIDProject ? `Project-${pIDProject}` : 'Customer';
		const tmpCached = this._cache[tmpKey];
		if (!pForce && tmpCached && (Date.now() - tmpCached.At) < CACHE_MILLISECONDS)
		{
			return tmpCached.Value;
		}
		const tmpURL = pIDProject ? `PlatformDashboard/Available/In/Project/${pIDProject}` : 'PlatformDashboard/Available';
		const tmpValue = await new Promise((fResolve, fReject) =>
		{
			this.pict.HeadlightRestClient.getJSON(tmpURL, (pError, pBody) =>
			{
				if (pError)
				{
					return fReject(pError);
				}
				if (!pBody || !Array.isArray(pBody.Dashboards))
				{
					return fReject(new Error((pBody && (pBody.error || pBody.Error)) || 'Unexpected response from PlatformDashboard/Available'));
				}
				fResolve(pBody);
			});
		});
		this._cache[tmpKey] = { At: Date.now(), Value: tmpValue };
		this.pict.AppData.HeadlightDashboards = tmpValue;
		return tmpValue;
	}

	/**
	 * @param {string} pURL - relative to the API, e.g. `Project/12`
	 * @return {Promise<any>}
	 */
	_getJSON(pURL)
	{
		return new Promise((fResolve, fReject) =>
		{
			this.pict.HeadlightRestClient.getJSON(pURL, (pError, pBody) =>
			{
				if (pError)
				{
					return fReject(pError);
				}
				fResolve(pBody);
			});
		});
	}

	/**
	 * @return {string|null} the browser storage key for this customer's recent projects
	 */
	_recentProjectsKey()
	{
		const tmpCustomerID = this._customerID();
		return tmpCustomerID ? `HeadlightDashboards-RecentProjects-${tmpCustomerID}` : null;
	}

	/**
	 * The projects this person last opened a dashboard for, most recent first. Kept in browser storage, so it is a
	 * convenience only: empty in a private window or a fresh browser.
	 *
	 * @return {Array<number>}
	 */
	recentProjects()
	{
		const tmpKey = this._recentProjectsKey();
		if (!tmpKey || (typeof window === 'undefined') || !window.localStorage)
		{
			return [];
		}
		try
		{
			const tmpValue = JSON.parse(window.localStorage.getItem(tmpKey) || '[]');
			return Array.isArray(tmpValue) ? tmpValue.map((pID) => parseInt(pID, 10)).filter((pID) => pID > 0) : [];
		}
		catch (pError)
		{
			return [];
		}
	}

	/**
	 * @param {number} pIDProject
	 */
	rememberProject(pIDProject)
	{
		const tmpKey = this._recentProjectsKey();
		if (!tmpKey || !pIDProject || (typeof window === 'undefined') || !window.localStorage)
		{
			return;
		}
		const tmpRecent = [ pIDProject ].concat(this.recentProjects().filter((pID) => pID !== pIDProject)).slice(0, RECENT_PROJECTS_LIMIT);
		try
		{
			window.localStorage.setItem(tmpKey, JSON.stringify(tmpRecent));
		}
		catch (pError)
		{
			this.log.warn(`HeadlightDashboardHost: could not remember project ${pIDProject}: ${pError.message || pError}`);
		}
	}

	/**
	 * One page of projects for the project picker: matches on name (which carries the project number), newest first.
	 * With no search, the first page leads with this person's recent projects.
	 *
	 * @param {string} pTerm
	 * @param {number} pPage
	 * @return {Promise<{ results: Array<{ Value: number, Text: string, Group?: string }>, hasMore: boolean }>}
	 */
	async searchProjects(pTerm, pPage)
	{
		const tmpTerm = String(pTerm === undefined || pTerm === null ? '' : pTerm).trim();
		const tmpPage = Number(pPage) || 0;
		const tmpOption = (pProject) => ({ Value: Number(pProject.IDProject), Text: pProject.Value || pProject.Name || `Project ${pProject.IDProject}` });
		let tmpFilter = `FBV~IDCustomer~EQ~${this._customerID()}~FBV~Deleted~EQ~0`;
		if (tmpTerm)
		{
			tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`;
		}
		const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}~FSF~IDProject~DESC~0/${tmpPage * PROJECT_PAGE_SIZE}/${PROJECT_PAGE_SIZE}`);
		const tmpProjects = Array.isArray(tmpRows) ? tmpRows : [];
		const tmpHasMore = tmpProjects.length >= PROJECT_PAGE_SIZE;
		const tmpRecentIDs = tmpTerm ? [] : this.recentProjects();
		if (tmpRecentIDs.length < 1)
		{
			return { results: tmpProjects.map(tmpOption), hasMore: tmpHasMore };
		}
		// Recent projects lead the first page; every page leaves them out of "All projects" so none is listed twice.
		const tmpOthers = tmpProjects.filter((pProject) => !tmpRecentIDs.includes(Number(pProject.IDProject))).map((pProject) => Object.assign(tmpOption(pProject), { Group: 'All projects' }));
		if (tmpPage > 0)
		{
			return { results: tmpOthers, hasMore: tmpHasMore };
		}
		const tmpRecentRows = await this._getJSON(`Projects/Lite/FilteredTo/FBL~IDProject~INN~${tmpRecentIDs.join(',')}~FBV~Deleted~EQ~0/0/${tmpRecentIDs.length}`).catch(() => []);
		const tmpRecentByID = new Map((Array.isArray(tmpRecentRows) ? tmpRecentRows : []).map((pProject) => [ Number(pProject.IDProject), pProject ]));
		const tmpRecent = tmpRecentIDs.filter((pID) => tmpRecentByID.has(pID)).map((pID) => Object.assign(tmpOption(tmpRecentByID.get(pID)), { Group: 'Recent' }));
		return { results: tmpRecent.concat(tmpOthers), hasMore: tmpHasMore };
	}

	/**
	 * Forget cached access answers (after a role change, or when the config screen saves rules).
	 */
	invalidate()
	{
		this._cache = {};
	}

	/**
	 * @param {string|Element} pDestination
	 * @return {Element|null}
	 */
	_element(pDestination)
	{
		if (typeof document === 'undefined')
		{
			return null;
		}
		return (typeof pDestination === 'string') ? document.querySelector(pDestination) : pDestination;
	}

	/**
	 * @param {string|Element} pDestination
	 * @param {string} pHTML
	 * @return {Element|null}
	 */
	_paint(pDestination, pHTML)
	{
		const tmpElement = this._element(pDestination);
		if (!tmpElement)
		{
			this.log.warn(`HeadlightDashboardHost: destination ${pDestination} not found.`);
			return null;
		}
		tmpElement.innerHTML = pHTML;
		if (this.actions)
		{
			this.actions.bindContainer(tmpElement);
		}
		return tmpElement;
	}

	/**
	 * @param {string} pTitle
	 * @param {string} pMessage
	 * @return {string}
	 */
	_noticeHTML(pTitle, pMessage)
	{
		return `<section class="section hld-notice"><div class="notification is-light"><p class="has-text-weight-semibold">${escapeHTML(pTitle)}</p><p>${escapeHTML(pMessage)}</p>` +
			`<p class="mt-3"><a href="#${escapeHTML(this.options.RouteBase)}/${CHOOSER_HASH}">All your dashboards</a></p></div></section>`;
	}

	/**
	 * Paint the list of dashboards this person may open.
	 *
	 * @param {string|Element} pDestination
	 * @param {{ IDProject?: number }} [pContext]
	 * @return {Promise<void>}
	 */
	async renderChooser(pDestination, pContext)
	{
		const tmpProjectID = (pContext && pContext.IDProject) || 0;
		let tmpAvailable;
		try
		{
			tmpAvailable = await this.loadAvailable(tmpProjectID);
		}
		catch (pError)
		{
			this._paint(pDestination, this._noticeHTML('Dashboards are unavailable right now', 'Try again in a minute. If this keeps happening, contact HeadLight support.'));
			return;
		}
		if (tmpAvailable.Dashboards.length < 1)
		{
			this._paint(pDestination, this._noticeHTML('No dashboards yet', 'None of your roles has a dashboard. Your administrator can add one under Dashboard Access in the configuration app.'));
			return;
		}
		/** @type {Record<string, Array<any>>} */
		const tmpByCategory = {};
		for (const tmpDashboard of tmpAvailable.Dashboards)
		{
			const tmpCategory = tmpDashboard.Category || 'Dashboards';
			(tmpByCategory[tmpCategory] = tmpByCategory[tmpCategory] || []).push(tmpDashboard);
		}
		let tmpHTML = '<section class="section hld-chooser">';
		for (const [ tmpCategory, tmpDashboards ] of Object.entries(tmpByCategory))
		{
			tmpHTML += `<h2 class="title is-5">${escapeHTML(tmpCategory)}</h2><div class="columns is-multiline">`;
			for (const tmpDashboard of tmpDashboards)
			{
				const tmpLink = ` data-hl-action="Dashboard.Open" data-hash="${escapeHTML(tmpDashboard.Hash)}"${tmpProjectID ? ` data-id-project="${tmpProjectID}"` : ''}`;
				tmpHTML += `<div class="column is-one-third"><a class="box hld-card"${tmpLink}>` +
					`<p class="has-text-weight-semibold">${escapeHTML(tmpDashboard.Title)}</p>` +
					`<p class="is-size-7">${escapeHTML(tmpDashboard.Description)}</p>` +
					((tmpDashboard.ProjectBound && !tmpProjectID) ? '<p class="is-size-7 has-text-grey mt-2">One project at a time</p>' : '') +
					'</a></div>';
			}
			tmpHTML += '</div>';
		}
		tmpHTML += '</section>';
		this._paint(pDestination, tmpHTML);
	}

	/**
	 * Open a dashboard into a container. With no hash, opens this person's landing dashboard, or the chooser.
	 *
	 * @param {string|Element} pDestination
	 * @param {{ Hash?: string, IDProject?: number, Filter?: string, FilterExperience?: string, Offset?: number, PageSize?: number }} [pRequest]
	 * @return {Promise<void>}
	 */
	async openInto(pDestination, pRequest)
	{
		const tmpRequest = pRequest || {};
		const tmpProjectID = parseInt(String(tmpRequest.IDProject || 0), 10) || 0;
		let tmpAvailable;
		try
		{
			tmpAvailable = await this.loadAvailable(tmpProjectID);
		}
		catch (pError)
		{
			this.log.error(`HeadlightDashboardHost: could not load available dashboards: ${pError.message || pError}`);
			this._paint(pDestination, this._noticeHTML('Dashboards are unavailable right now', 'Try again in a minute. If this keeps happening, contact HeadLight support.'));
			return;
		}
		const tmpHash = tmpRequest.Hash || tmpAvailable.Landing;
		if (!tmpHash)
		{
			// No landing dashboard for this person: an app with its own home page (LIMS → Samples) goes there instead.
			const tmpFallback = (typeof this.options.getFallbackRoute === 'function') ? this.options.getFallbackRoute() : null;
			if (tmpFallback && this.pict.providers.PictRouter)
			{
				this.pict.providers.PictRouter.navigate(tmpFallback);
				return;
			}
			return this.renderChooser(pDestination, { IDProject: tmpProjectID });
		}
		const tmpDashboard = tmpAvailable.Dashboards.find((pDashboard) => pDashboard.Hash === tmpHash);
		if (!tmpDashboard)
		{
			this._paint(pDestination, this._noticeHTML('This dashboard isn\'t available to you', 'None of your roles includes it. If you need it, ask your administrator to add it under Dashboard Access.'));
			return;
		}
		if (tmpDashboard.ProjectBound && !tmpProjectID)
		{
			// Reopen the project they last worked in, in place: navigating would put a step in history that back can't pass.
			const tmpRecentProjectID = this.recentProjects()[0];
			if (tmpRecentProjectID)
			{
				return this.openInto(pDestination, Object.assign({}, tmpRequest, { Hash: tmpHash, IDProject: tmpRecentProjectID }));
			}
			if (this._paint(pDestination, this._frameHTML(tmpDashboard, 0, tmpAvailable)))
			{
				this._mountProjectPicker(tmpDashboard, 0);
			}
			return;
		}
		const tmpFrame = this._paint(pDestination, this._frameHTML(tmpDashboard, tmpProjectID, tmpAvailable));
		if (!tmpFrame)
		{
			return;
		}
		if (tmpDashboard.ProjectBound)
		{
			this.rememberProject(tmpProjectID);
			this._mountProjectPicker(tmpDashboard, tmpProjectID);
		}
		if (tmpDashboard.Kind === 'Legacy')
		{
			return this._openLegacy(tmpDashboard, tmpProjectID);
		}
		return this._openDynamic(tmpDashboard, tmpProjectID, tmpRequest.FilterExperience || '', { Filter: tmpRequest.Filter || '', Offset: parseInt(String(tmpRequest.Offset || 0), 10) || 0, PageSize: parseInt(String(tmpRequest.PageSize || 0), 10) || 0 });
	}

	/**
	 * A thin bar across the top of an open dashboard: back to the list, and straight to this person's other dashboards.
	 *
	 * @param {any} pDashboard
	 * @param {number} pIDProject
	 * @param {any} [pAvailable] - The PlatformDashboard/Available response.
	 * @return {string} '' when this is their only dashboard
	 */
	_switcherHTML(pDashboard, pIDProject, pAvailable)
	{
		const tmpOthers = ((pAvailable && Array.isArray(pAvailable.Dashboards)) ? pAvailable.Dashboards : []).filter((pOther) => pOther.Hash !== pDashboard.Hash);
		if (tmpOthers.length < 1)
		{
			return '';
		}
		const tmpProjectAttribute = pIDProject ? ` data-id-project="${pIDProject}"` : '';
		const tmpLinks = (tmpOthers.length <= MAX_SWITCHER_LINKS) ?
			tmpOthers.map((pOther) => `<a data-hl-action="Dashboard.Open" data-hash="${escapeHTML(pOther.Hash)}"${tmpProjectAttribute}>${escapeHTML(pOther.Title)}</a>`).join('<span class="hld-bar-dot" aria-hidden="true">·</span>') : '';
		return `<nav class="hld-bar" aria-label="Your dashboards">` +
			`<a class="hld-bar-all" href="#${escapeHTML(this.routeFor(CHOOSER_HASH, pIDProject))}"><span class="icon is-small"><i class="fas fa-table-cells-large" aria-hidden="true"></i></span><span>All your dashboards</span></a>` +
			(tmpLinks ? `<span class="hld-bar-others"><span class="hld-bar-label">Switch to</span>${tmpLinks}</span>` : '') +
			'</nav>';
	}

	/**
	 * @param {any} pDashboard
	 * @param {number} [pIDProject]
	 * @param {any} [pAvailable] - The PlatformDashboard/Available response.
	 * @return {string}
	 */
	_frameHTML(pDashboard, pIDProject, pAvailable)
	{
		const tmpNotices = libDashboardAccess.describeUnresolved(pDashboard.Unresolved)
			.map((pNotice) => `<div class="notification is-warning is-light hld-unresolved"><p class="has-text-weight-semibold">${escapeHTML(pNotice.Title)}</p><p>${escapeHTML(pNotice.Message)}</p></div>`)
			.join('');
		if (Array.isArray(pDashboard.Unresolved) && pDashboard.Unresolved.length > 0)
		{
			this.log.info(`HeadlightDashboardHost: ${pDashboard.Hash} opened without ${pDashboard.Unresolved.map((pClause) => libDashboardAccess.describeClause(pClause)).join(' and ')} (no value for this person).`);
		}
		const tmpHiddenActions = this.actions ? this.actions.unavailableActionsCSS('.hld-frame') : '';
		const tmpProjectBar = pDashboard.ProjectBound ?
			`<div class="hld-project-bar"><span class="hld-project-label">Project</span><div id="${PROJECT_PICKER_ID}" class="hld-project-picker"></div></div>` : '';
		const tmpBody = (pDashboard.ProjectBound && !pIDProject) ?
			`<div class="hld-project-prompt"><h1 class="title is-4">${escapeHTML(pDashboard.Title)}</h1><p>Shows one project at a time. Choose a project to see it.</p></div>` : '';
		return `<div class="hld-frame${pDashboard.ProjectBound ? ' is-project-bound' : ''}">` +
			(tmpHiddenActions ? `<style>${tmpHiddenActions}</style>` : '') +
			this._switcherHTML(pDashboard, pIDProject || 0, pAvailable) +
			tmpProjectBar +
			tmpNotices +
			`<div id="${BODY_ID}">${tmpBody}</div></div>`;
	}

	/**
	 * Put the project picker in a project-bound dashboard's frame. Choosing a project opens the dashboard for it.
	 *
	 * @param {any} pDashboard
	 * @param {number} pIDProject - 0 when none is chosen yet; the list then opens straight away.
	 */
	_mountProjectPicker(pDashboard, pIDProject)
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function'))
		{
			this.log.warn('HeadlightDashboardHost: Pict-Section-Picker is not registered, so there is no project picker.');
			return;
		}
		this.pict.AppData.HeadlightDashboardProject = { IDProject: pIDProject || 0 };
		const tmpView = tmpPicker.createPicker(PROJECT_PICKER_ID,
			{
				DestinationAddress: `#${PROJECT_PICKER_ID}`,
				ValueAddress: 'AppData.HeadlightDashboardProject.IDProject',
				Searchable: true,
				AllowClear: false,
				EmptyValues: [ 0 ],
				Placeholder: 'Search projects by name or number…',
				EmptyLabel: 'No projects match',
				DataProvider: (pTerm, pPage) => this.searchProjects(pTerm, pPage).catch((pError) =>
				{
					this.log.error(`HeadlightDashboardHost: project search failed: ${pError.message || pError}`);
					return { results: [], hasMore: false };
				}),
				ResolveValue: (pValue) =>
				{
					const tmpID = Number(pValue);
					if (!tmpID)
					{
						return Promise.resolve(null);
					}
					return this._getJSON(`Project/${tmpID}`)
						.then((pProject) => ({ Value: tmpID, Text: (pProject && pProject.Name) || `Project ${tmpID}` }))
						.catch(() => ({ Value: tmpID, Text: `Project ${tmpID}` }));
				},
				OnChange: (pValue) =>
				{
					const tmpID = parseInt(pValue, 10) || 0;
					if (tmpID && tmpID !== pIDProject)
					{
						this.rememberProject(tmpID);
						this.navigateTo(pDashboard.Hash, tmpID);
					}
				},
			});
		if (!tmpView || (typeof tmpView.render !== 'function'))
		{
			return;
		}
		tmpView.render();
		if (!pIDProject && (typeof tmpView.open === 'function'))
		{
			tmpView.open();
		}
	}

	/**
	 * @param {any} pDashboard
	 * @param {number} pIDProject
	 * @param {string} pFilterExperience
	 * @param {{ Filter?: string, Offset?: number, PageSize?: number }} [pPage]
	 * @return {Promise<void>}
	 */
	async _openDynamic(pDashboard, pIDProject, pFilterExperience, pPage)
	{
		if (!this.loader)
		{
			this.log.error('HeadlightDashboardHost: DynamicDashboardLoader provider is not registered.');
			return;
		}
		const tmpExtra = [];
		if (pDashboard.ProjectBound && pDashboard.ProjectField && pIDProject)
		{
			tmpExtra.push(`FBV~${pDashboard.ProjectField}~EQ~${pIDProject}`);
		}
		if (pDashboard.DataFilterString)
		{
			tmpExtra.push(pDashboard.DataFilterString);
		}
		const tmpDrill = this._pendingDrillFilters[pDashboard.Hash];
		if (tmpDrill)
		{
			delete this._pendingDrillFilters[pDashboard.Hash];
			const tmpResolved = libDashboardAccess.resolveClauses(tmpDrill, { User: {}, Context: { IDProject: pIDProject } });
			const tmpDrillString = libDashboardAccess.compileFilter(tmpResolved.Clauses);
			if (tmpDrillString)
			{
				tmpExtra.push(tmpDrillString);
			}
		}
		let tmpEntry;
		try
		{
			tmpEntry = await this.loader.prepareDashboard(pDashboard.Hash, this._customerID(), { ExtraFilter: tmpExtra.join('~'), URLTemplate: this.routeFor(pDashboard.Hash, pIDProject), ShowDescription: this._showDescriptions() });
		}
		catch (pError)
		{
			this.log.error(`HeadlightDashboardHost: could not prepare ${pDashboard.Hash}: ${pError.message || pError}`);
		}
		if (!tmpEntry)
		{
			this._paint(`#${BODY_ID}`, this._noticeHTML(pDashboard.Title, 'This dashboard could not be loaded. Contact HeadLight support if it keeps happening.'));
			return;
		}
		const tmpRecordSet = this.pict.PictSectionRecordSet;
		// A list's paging and search links go to its own /PSRS/<RecordSet>/List routes; a micro app has to allow them.
		if (typeof this.options.onRecordSetPrepared === 'function')
		{
			this.options.onRecordSetPrepared(tmpEntry.RecordSet);
		}
		const tmpIsList = (tmpEntry.Mode === 'List');
		if (!tmpIsList)
		{
			await this.loader.whenProviderReady(tmpEntry.RecordSet);
		}
		const tmpView = tmpIsList ? tmpRecordSet.childViews.list : tmpRecordSet.childViews.dashboard;
		const tmpConfiguration = tmpRecordSet.recordSetProviderConfigurations[tmpEntry.RecordSet];
		const tmpDataSource = tmpEntry.DataSource || {};
		const tmpPrevious = tmpView.options.DefaultDestinationAddress;
		tmpView.options.DefaultDestinationAddress = `#${BODY_ID}`;
		try
		{
			if (tmpIsList)
			{
				this._keepListInHost(pDashboard, pIDProject, tmpEntry.RecordSet);
				const tmpListFilter = composeListFilter(tmpEntry.ScopeFilter || '', pPage && pPage.Filter, !!(pPage && pPage.PageSize), this._lastListFilters[tmpEntry.RecordSet]);
				this._lastListFilters[tmpEntry.RecordSet] = tmpListFilter;
				await tmpView.renderList(tmpConfiguration, `RSP-Provider-${tmpEntry.RecordSet}`, tmpListFilter, pFilterExperience,
					(pPage && pPage.Offset) || 0, (pPage && pPage.PageSize) || DEFAULT_LIST_PAGE_SIZE, false);
			}
			else
			{
				await tmpView.renderSpecificDashboard(pDashboard.Hash, tmpConfiguration, `RSP-Provider-${tmpEntry.RecordSet}`,
					tmpDataSource.ScopeFilter || '', pFilterExperience, 0, tmpDataSource.PageSize || 1000);
			}
		}
		finally
		{
			tmpView.options.DefaultDestinationAddress = tmpPrevious;
		}
	}

	/**
	 * A list's search, sort and page links go to its own `/PSRS/<RecordSet>/List` route, outside the host: the frame, its
	 * notices and dashboard links would be gone, and a micro app refuses that route on a fresh load. Send them to the
	 * host's route instead. Page links are anchors (caught on click); search and sort navigate through the router.
	 *
	 * @param {any} pDashboard
	 * @param {number} pIDProject
	 * @param {string} pRecordSet
	 */
	_keepListInHost(pDashboard, pIDProject, pRecordSet)
	{
		this._listDashboards[pRecordSet] = { Hash: pDashboard.Hash, IDProject: pIDProject || 0 };
		this._installListRouteRedirect();
		const tmpBody = this._element(`#${BODY_ID}`);
		if (!tmpBody)
		{
			return;
		}
		tmpBody.addEventListener('click', (pEvent) =>
		{
			const tmpTarget = /** @type {any} */ (pEvent.target);
			const tmpLink = tmpTarget && (typeof tmpTarget.closest === 'function') ? tmpTarget.closest('a[href]') : null;
			const tmpRoute = tmpLink ? this._redirectListRoute(tmpLink.getAttribute('href')) : null;
			if (!tmpRoute)
			{
				return;
			}
			pEvent.preventDefault();
			const tmpRouter = this.pict.providers.PictRouter;
			if (tmpRouter && typeof tmpRouter.navigate === 'function')
			{
				tmpRouter.navigate(tmpRoute);
			}
		});
	}

	/**
	 * @param {string} pRoute
	 * @return {string|null} the host route for a list dashboard's own list route, or null when it isn't one
	 */
	_redirectListRoute(pRoute)
	{
		const tmpMatch = /^#?\/PSRS\/([^/]+)\/List(?:\/|$)/.exec(typeof pRoute === 'string' ? pRoute : '');
		const tmpDashboard = tmpMatch ? this._listDashboards[tmpMatch[1]] : null;
		const tmpLink = tmpDashboard ? parseListLink(pRoute, tmpMatch[1]) : null;
		if (!tmpLink)
		{
			return null;
		}
		return this.routeFor(tmpDashboard.Hash, tmpDashboard.IDProject, tmpLink);
	}

	/**
	 * Every router navigation (pict-section-recordset's search and sort included) passes through here once a list
	 * dashboard has opened.
	 */
	_installListRouteRedirect()
	{
		const tmpRouter = this.pict.providers.PictRouter && this.pict.providers.PictRouter.router;
		if (this._listRouteRedirectInstalled || !tmpRouter || (typeof tmpRouter.navigate !== 'function'))
		{
			return;
		}
		this._listRouteRedirectInstalled = true;
		const fNavigate = tmpRouter.navigate.bind(tmpRouter);
		tmpRouter.navigate = (pRoute, ...pRest) => fNavigate(this._redirectListRoute(pRoute) || pRoute, ...pRest);
	}

	/**
	 * The legacy app_dashboards bundles are their own pages; frame them and translate their sample request into an
	 * action. The session cookie is scoped to the parent domain, so the frame is signed in already.
	 *
	 * @param {any} pDashboard
	 * @param {number} pIDProject
	 */
	_openLegacy(pDashboard, pIDProject)
	{
		const tmpConfig = (pDashboard.Legacy && pDashboard.Legacy.Config) || {};
		const tmpQuery = [ `projectID=${pIDProject}` ];
		if (tmpConfig.ShowAddSample)
		{
			tmpQuery.push('addNewSamples');
		}
		const tmpURL = `${this.legacyDashboardsBaseURL()}/dashboards/${encodeURIComponent(pDashboard.Hash)}/index.html?${tmpQuery.join('&')}`;
		this._paint(`#${BODY_ID}`, `<iframe class="hld-legacy-frame" title="${escapeHTML(pDashboard.Title)}" src="${escapeHTML(tmpURL)}" style="width:100%;min-height:80vh;border:0;"></iframe>`);
		this._installLegacyListener();
	}

	/**
	 * @return {string} e.g. https://dashboards.qa.headlight.com for a page on lims.qa.headlight.com (no trailing slash)
	 */
	legacyDashboardsBaseURL()
	{
		if (this.options.LegacyDashboardsBaseURL)
		{
			return this.options.LegacyDashboardsBaseURL;
		}
		return this.appBaseURL('dashboards');
	}

	/**
	 * @param {string} pSubdomain - e.g. 'lims', 'dashboards'
	 * @return {string} that app's base URL in this page's environment, no trailing slash
	 */
	appBaseURL(pSubdomain)
	{
		const tmpHostname = (typeof window !== 'undefined' && window.location) ? window.location.hostname : '';
		return libHosts.getAppBaseURL(pSubdomain, libHosts.getEnvironmentFromHostname(tmpHostname)).replace(/\/$/, '');
	}

	_installLegacyListener()
	{
		if (this._legacyListenerInstalled || typeof window === 'undefined')
		{
			return;
		}
		this._legacyListenerInstalled = true;
		window.addEventListener('message', (pEvent) =>
		{
			if (!pEvent || pEvent.origin !== this.legacyDashboardsBaseURL())
			{
				return;
			}
			const tmpAction = libActions.actionFromLegacySamplePayload(pEvent.data);
			if (tmpAction && this.actions)
			{
				this.actions.dispatch(tmpAction);
			}
		});
	}
}

module.exports = HeadlightDashboardHost;
module.exports.default_configuration = { ProviderIdentifier: 'HeadlightDashboardHost' };
module.exports.routePatterns = routePatterns;
module.exports.parseListLink = parseListLink;
module.exports.composeListFilter = composeListFilter;
module.exports.BODY_ID = BODY_ID;
module.exports.CHOOSER_HASH = CHOOSER_HASH;
